import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fetchProducts } from '../lib/api';
import { swatchFor } from '../lib/colors';
import Reveal from './Reveal';
import Magnetic from './motion/Magnetic';

const CYCLE_MS = 2800;

const keyFor = (c) =>
  /black/i.test(c) ? 'black'
  : /brown/i.test(c) ? 'brown'
  : /auburn/i.test(c) ? 'auburn'
  : /wine|burgundy/i.test(c) ? 'burgundy'
  : /ginger|copper/i.test(c) ? 'ginger'
  : /blonde/i.test(c) ? 'blonde'
  : /\bred\b/i.test(c) ? 'red' : '';

const ShadeSpotlight = () => {
  const [product, setProduct] = useState(null);
  const [idx, setIdx] = useState(0);
  const [manualAt, setManualAt] = useState(0);

  useEffect(() => {
    fetchProducts('bundles')
      .then((list) => setProduct(list.find((p) => p.color_images && Object.keys(p.color_images).length >= 9) || null))
      .catch(() => {});
  }, []);

  const shades = useMemo(() => {
    if (!product) return [];
    return (product.colors || []).filter((c) => product.color_images[c]).slice(0, 9);
  }, [product]);

  useEffect(() => {
    if (shades.length < 2) return undefined;
    const iv = setInterval(() => setIdx((i) => (i + 1) % shades.length), CYCLE_MS);
    return () => clearInterval(iv);
  }, [shades.length, manualAt]);

  if (!product || !shades.length) return null;
  const color = shades[idx];

  return (
    <section className="relative bg-[#0b0b0b] py-20 sm:py-28 overflow-hidden" data-testid="shade-spotlight">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(198,161,91,0.14),transparent_55%)]" />
      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div>
            <p className="overline text-gold-gradient mb-3">Shade Spotlight</p>
            <h2 className="font-display text-4xl sm:text-6xl text-[#f7f3ec] leading-tight">Nine Signature Shades</h2>
            <div className="gold-line w-20 my-6" />
            <p className="font-body text-[#cfc7b8] leading-relaxed max-w-md mb-6">
              From jet black to bright blonde — every bundle comes in nine hand-matched shades. Tap a swatch to see it, then shop it.
            </p>
            <AnimatePresence mode="wait">
              <motion.p key={color} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}
                className="font-display italic text-2xl sm:text-3xl text-gold-gradient mb-7" data-testid="spotlight-shade-name">
                {color}
              </motion.p>
            </AnimatePresence>
            <div className="flex flex-wrap items-center gap-3.5 mb-9" data-testid="spotlight-swatches">
              {shades.map((c, i) => (
                <button key={c} type="button" title={c} data-testid={`spotlight-swatch-${i}`}
                  onClick={() => { setIdx(i); setManualAt(Date.now()); }}
                  className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${i === idx ? 'shade-glow border-[#e7d3a1] scale-110' : 'border-white/20'}`}
                  style={{ background: swatchFor(c) }} />
              ))}
            </div>
            <Magnetic className="inline-block">
              <Link to={`/shop/bundles?shade=${keyFor(color)}`} className="btn-gold inline-flex px-8 py-3.5" data-testid="spotlight-shop-btn">
                <span className="flex items-center gap-2">Shop This Shade <ArrowRight size={15} /></span>
              </Link>
            </Magnetic>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <Link to={`/shop/bundles?shade=${keyFor(color)}`} className="block relative aspect-square max-w-[560px] mx-auto w-full" data-testid="spotlight-image-link">
            <div className="absolute -inset-3 border border-[#c6a15b]/25 pointer-events-none" />
            <AnimatePresence mode="sync">
              <motion.img key={color} src={product.color_images[color]} alt={`${color} shade`}
                initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover" />
            </AnimatePresence>
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default ShadeSpotlight;
